import React, { ChangeEvent, Fragment, FunctionComponent } from "react";

import { Caption, GridColumnCount, GridGapSize, GridLayout, GridLayoutItem } from "@multiweb/reactComponents";
import { LOC_ADDRESS_TYPE, LOC_ADDRESSEE, LOC_AREA_OF_COMPETENCE, LOC_ATTRIBUTE_NAME, LOC_ATTRIBUTE_VALUE, LOC_BASIC_CONDITION, LOC_CLASS, LOC_COMMENT, LOC_CURRENT_PARTICIPANTS, LOC_CURRENT_USER, LOC_DATA_CONDITION, LOC_ENTERED_DIRECTLY, LOC_FOLLOWING_PARTICIPANTS, LOC_FROM_OTHER_RECORDS, LOC_OWNER, LOC_ROLE, LOC_TEXT } from "../../../../../../locKeys";
import { SendSmsRecipientType } from "../../../../../../workflowModel/Actions";
import { CONST_ACTION_COMMENT_AT, CONST_ACTION_CONDITION_AT, CONST_SEND_SMS_ATTR_NAME_AT, CONST_SEND_SMS_CLASS_NAME_AT, CONST_SEND_SMS_DATA_CONDITION_AT, CONST_SEND_SMS_RECIPIENT_AT, CONST_SEND_SMS_RECIPIENT_TYPE_AT, CONST_SEND_SMS_TEXT_AT } from "../../../../../../workflowParser/constants";
import { Edit, Memo, WfOption, WfSelect } from "../../../../components";
import { MAX_INPUT_LENGTH_L, MAX_INPUT_LENGTH_XXL } from "../constants";
import { ActionPageProps } from "../types";

export const SendSmsPage: FunctionComponent<ActionPageProps> = ({ properties, updateAction, hostKey }) => {
    const recipientType = properties[CONST_SEND_SMS_RECIPIENT_TYPE_AT];
    const isRecipientConditionType = recipientType === SendSmsRecipientType.condition;
    const isRecipientShown = recipientType !== SendSmsRecipientType.actualUser
        && recipientType !== SendSmsRecipientType.owner
        && recipientType !== SendSmsRecipientType.participants
        && recipientType !== SendSmsRecipientType.nextParticipants
        && recipientType !== SendSmsRecipientType.condition;

    const onRecipientTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        updateAction([{ key: CONST_SEND_SMS_RECIPIENT_AT, value: "" }, { key: CONST_SEND_SMS_RECIPIENT_TYPE_AT, value: e.target.value }]);
    };

    const onRecipientChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_SMS_RECIPIENT_AT, value: e.target.value }]);
    };

    const onClassNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_SMS_CLASS_NAME_AT, value: e.target.value }]);
    };

    const onAttrNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_SMS_ATTR_NAME_AT, value: e.target.value }]);
    };

    const onDataConditionChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateAction([{ key: CONST_SEND_SMS_DATA_CONDITION_AT, value: e.target.value }]);
    };

    const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_SEND_SMS_TEXT_AT, value: e.target.value }]);
    };

    const onConditionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_ACTION_CONDITION_AT, value: e.target.value }]);
    };

    const onCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateAction([{ key: CONST_ACTION_COMMENT_AT, value: e.target.value }]);
    };

    return (
        <GridLayout columns={GridColumnCount.one} gap={GridGapSize.two}>
            <GridLayoutItem>
                <Caption locKey={LOC_ADDRESS_TYPE} />
                <WfSelect qa={LOC_ADDRESS_TYPE} value={properties[CONST_SEND_SMS_RECIPIENT_TYPE_AT]} onChange={onRecipientTypeChange} {...{ hostKey }}>
                    <WfOption qa={SendSmsRecipientType.value} value={SendSmsRecipientType.value} locKey={LOC_ENTERED_DIRECTLY} />
                    <WfOption qa={SendSmsRecipientType.attributeValue} value={SendSmsRecipientType.attributeValue} locKey={LOC_ATTRIBUTE_VALUE} />
                    <WfOption qa={SendSmsRecipientType.actualUser} value={SendSmsRecipientType.actualUser} locKey={LOC_CURRENT_USER} />
                    <WfOption qa={SendSmsRecipientType.owner} value={SendSmsRecipientType.owner} locKey={LOC_OWNER} />
                    <WfOption qa={SendSmsRecipientType.participants} value={SendSmsRecipientType.participants} locKey={LOC_CURRENT_PARTICIPANTS} />
                    <WfOption qa={SendSmsRecipientType.nextParticipants} value={SendSmsRecipientType.nextParticipants} locKey={LOC_FOLLOWING_PARTICIPANTS} />
                    <WfOption qa={SendSmsRecipientType.role} value={SendSmsRecipientType.role} locKey={LOC_ROLE} />
                    <WfOption qa={SendSmsRecipientType.okruh} value={SendSmsRecipientType.okruh} locKey={LOC_AREA_OF_COMPETENCE} />
                    <WfOption qa={SendSmsRecipientType.condition} value={SendSmsRecipientType.condition} locKey={LOC_FROM_OTHER_RECORDS} />
                </WfSelect>
            </GridLayoutItem>
            {isRecipientShown &&
                <GridLayoutItem>
                    <Caption required locKey={LOC_ADDRESSEE} />
                    <Edit qa={LOC_ADDRESSEE} required maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_SMS_RECIPIENT_AT]} onChange={onRecipientChange} {...{ hostKey }} />
                </GridLayoutItem>
            }
            {isRecipientConditionType &&
                <Fragment>
                    <GridLayoutItem>
                        <Caption required locKey={LOC_CLASS} />
                        <Edit qa={LOC_CLASS} required maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_SMS_CLASS_NAME_AT]} onChange={onClassNameChange} {...{ hostKey }} />
                    </GridLayoutItem>
                    <GridLayoutItem>
                        <Caption required locKey={LOC_ATTRIBUTE_NAME} />
                        <Edit qa={LOC_ATTRIBUTE_NAME} required maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_SEND_SMS_ATTR_NAME_AT]} onChange={onAttrNameChange} {...{ hostKey }} />
                    </GridLayoutItem>
                    <GridLayoutItem>
                        <Caption locKey={LOC_DATA_CONDITION} />
                        <Edit qa={LOC_DATA_CONDITION} maxLength={MAX_INPUT_LENGTH_XXL} value={properties[CONST_SEND_SMS_DATA_CONDITION_AT]} onChange={onDataConditionChange} {...{ hostKey }} />
                    </GridLayoutItem>
                </Fragment>
            }
            <GridLayoutItem>
                <Caption required locKey={LOC_TEXT} />
                <Memo qa={LOC_TEXT} required rows={10} value={properties[CONST_SEND_SMS_TEXT_AT]} onChange={onTextChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem>
                <Caption locKey={LOC_BASIC_CONDITION} />
                <Memo qa={LOC_BASIC_CONDITION} value={properties[CONST_ACTION_CONDITION_AT]} onChange={onConditionChange} {...{ hostKey }} />
            </GridLayoutItem>
            <GridLayoutItem>
                <Caption locKey={LOC_COMMENT} />
                <Memo qa={LOC_COMMENT} value={properties[CONST_ACTION_COMMENT_AT]} onChange={onCommentChange} {...{ hostKey }} />
            </GridLayoutItem>
        </GridLayout>
    );
};
